import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { NotificationsInboxProvider } from '../../context/NotificationsInboxContext';
import NotificationsDropdown from './NotificationsDropdown';
import './Header.css';

const LOGO_SRC = `${process.env.PUBLIC_URL}/gobag.svg`;

const IconBag = () => (
  <svg className="header-pill__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);

const IconSearch = () => (
  <svg className="header-pill__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const IconQuestion = () => (
  <svg className="header-pill__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconEarnings = () => (
  <svg className="header-pill__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
  </svg>
);

const IconUserPlus = () => (
  <svg className="header-signup-btn__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

const IconUser = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotifDesktopOpen, setIsNotifDesktopOpen] = useState(false);
  const [isNotifMobileOpen, setIsNotifMobileOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const { user, logout, isAuthenticated, openAuthModal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);

  const exploreActive =
    location.pathname === '/explore' || location.pathname.startsWith('/explore/');
  const howItWorksActive = location.pathname === '/how-it-works';

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const openLogoutModal = () => {
    setShowLogoutModal(true);
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
  };

  const closeLogoutModal = () => {
    setShowLogoutModal(false);
  };

  const confirmLogout = async () => {
    setShowLogoutModal(false);
    await logout();
    navigate('/explore');
  };

  useEffect(() => {
    if (!showLogoutModal) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowLogoutModal(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showLogoutModal]);

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const getUserDisplayName = () => {
    return user?.full_name || user?.username || user?.email?.split('@')[0] || 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserProfilePicture = () => {
    return user?.profile_picture_url || null;
  };

  return (
    <NotificationsInboxProvider user={isAuthenticated ? user : null}>
    <header className={`header ${isAuthenticated ? 'header--authed' : 'header--guest'}`}>
      <div className="container header-container">
        <div className="header-content">
          <div className="header-zone header-zone--left">
            <Link to="/explore" className="logo">
              {!logoFailed ? (
                <img
                  src={LOGO_SRC}
                  alt="GoBag"
                  className="logo-image"
                  width={194}
                  height={40}
                  onError={() => setLogoFailed(true)}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <>
                  <div className="logo-icon" aria-hidden>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <rect width="32" height="32" rx="8" fill="currentColor" />
                      <path d="M8 12h16v12a2 2 0 01-2 2H10a2 2 0 01-2-2V12z" fill="white" />
                      <path d="M12 8a4 4 0 018 0v4H12V8z" stroke="white" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                  <span className="logo-text logo-text--fallback">GoBag</span>
                </>
              )}
            </Link>
          </div>

          <div className="header-zone header-zone--center">
            {!isAuthenticated ? (
              <nav className="header-pills" aria-label="Main">
                <Link
                  to="/explore"
                  className={`header-pill ${exploreActive ? 'header-pill--active' : 'header-pill--outline'}`}
                >
                  <span className="header-pill__icon-wrap" aria-hidden>
                    <IconSearch />
                  </span>
                  <span className="header-pill__label">Explore</span>
                </Link>
                <Link
                  to="/how-it-works"
                  className={`header-pill ${howItWorksActive ? 'header-pill--active' : 'header-pill--outline'}`}
                >
                  <span className="header-pill__icon-wrap" aria-hidden>
                    <IconQuestion />
                  </span>
                  <span className="header-pill__label">How it Works</span>
                </Link>
              </nav>
            ) : (
              <nav className="header-pills" aria-label="Main">
                <Link
                  to="/bags"
                  className={`header-pill ${location.pathname === '/bags' ? 'header-pill--active' : ''}`}
                >
                  <span className="header-pill__icon-wrap" aria-hidden>
                    <IconBag />
                  </span>
                  <span className="header-pill__label">My Bags</span>
                </Link>
                <Link to="/explore" className={`header-pill ${exploreActive ? 'header-pill--active' : ''}`}>
                  <span className="header-pill__icon-wrap" aria-hidden>
                    <IconSearch />
                  </span>
                  <span className="header-pill__label">Explore</span>
                </Link>
                <Link
                  to="/earnings"
                  className={`header-pill ${location.pathname === '/earnings' ? 'header-pill--active' : ''}`}
                >
                  <span className="header-pill__icon-wrap" aria-hidden>
                    <IconEarnings />
                  </span>
                  <span className="header-pill__label">Earnings</span>
                </Link>
                <Link
                  to="/how-it-works"
                  className={`header-pill ${howItWorksActive ? 'header-pill--active' : ''}`}
                >
                  <span className="header-pill__icon-wrap" aria-hidden>
                    <IconQuestion />
                  </span>
                  <span className="header-pill__label">How it Works</span>
                </Link>
              </nav>
            )}
          </div>

          <div className="header-zone header-zone--right">
            {!isAuthenticated ? (
              <div className="header-auth-desktop">
                <button type="button" className="header-signin-btn" onClick={() => openAuthModal('signin')}>
                  <span>Sign In</span>
                  <IconUser />
                </button>
                <button type="button" className="header-signup-btn" onClick={() => openAuthModal('signup')}>
                  <IconUserPlus />
                  <span>Sign Up</span>
                </button>
              </div>
            ) : (
              <>
                <NotificationsDropdown
                  isOpen={isNotifDesktopOpen}
                  onToggle={() => {
                    setIsNotifDesktopOpen((v) => !v);
                    setIsNotifMobileOpen(false);
                  }}
                  onClose={() => setIsNotifDesktopOpen(false)}
                />
                <div className="header-user-slot" ref={userMenuRef}>
                  <button
                    type="button"
                    className="user-menu-trigger user-menu-trigger--avatar-only"
                    onClick={toggleUserMenu}
                    aria-expanded={isUserMenuOpen}
                    aria-haspopup="true"
                    aria-label="Account menu"
                  >
                    <div className="user-avatar user-avatar--header">
                      {getUserProfilePicture() ? (
                        <img
                          src={getUserProfilePicture()}
                          alt={getUserDisplayName()}
                          className="user-avatar-image"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        getUserInitials()
                      )}
                    </div>
                  </button>
                  {isUserMenuOpen && (
                    <div className="user-menu-dropdown" role="menu">
                      <div className="user-menu-dropdown-profile">
                        <div className="user-menu-dropdown-avatar" aria-hidden="true">
                          {getUserProfilePicture() ? (
                            <img src={getUserProfilePicture()} alt="" className="user-menu-dropdown-avatar-img" loading="lazy" decoding="async" />
                          ) : (
                            <span className="user-menu-dropdown-avatar-initials">{getUserInitials()}</span>
                          )}
                        </div>
                        <div className="user-menu-dropdown-meta">
                          <span className="user-menu-dropdown-name">{getUserDisplayName()}</span>
                          <span className="user-menu-dropdown-email">{user?.email || ''}</span>
                        </div>
                      </div>
                      <Link
                        to="/profile"
                        className="user-menu-dropdown-item"
                        role="menuitem"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <span className="user-menu-dropdown-item-icon" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M10 10.833a3.333 3.333 0 100-6.666 3.333 3.333 0 000 6.666z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M3.333 17.5v-.833A5.833 5.833 0 0110 10.833a5.833 5.833 0 015.833 5.834v.833"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span>Profile &amp; settings</span>
                      </Link>
                      {user?.is_admin && (
                        <Link
                          to="/admin/featured"
                          className="user-menu-dropdown-item"
                          role="menuitem"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <span className="user-menu-dropdown-item-icon" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M10 2.5l2.12 4.29 4.73.69-3.42 3.33.81 4.71L10 13.24 5.76 15.52l.81-4.71L3.15 7.48l4.73-.69L10 2.5z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span>Featured bags</span>
                        </Link>
                      )}
                      {user?.is_admin && (
                        <Link
                          to="/admin/properties"
                          className="user-menu-dropdown-item"
                          role="menuitem"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <span className="user-menu-dropdown-item-icon" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M3 5.5A2.5 2.5 0 015.5 3h9A2.5 2.5 0 0117 5.5v9a2.5 2.5 0 01-2.5 2.5h-9A2.5 2.5 0 013 14.5v-9z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              />
                              <path
                                d="M7 13l2-5 2 3 1.5-2L14 12"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span>Properties</span>
                        </Link>
                      )}
                      <button
                        type="button"
                        className="user-menu-dropdown-item user-menu-dropdown-item--logout"
                        role="menuitem"
                        onClick={openLogoutModal}
                      >
                        <span className="user-menu-dropdown-item-icon user-menu-dropdown-item-icon--logout" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M7.5 17.5H4.167A.833.833 0 013.333 16.667V3.333A.833.833 0 014.167 2.5H7.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M13.333 14.167L16.667 10l-3.334-3.333M7.5 10h9.167"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            <button className="mobile-menu-toggle" type="button" onClick={toggleMenu} aria-label="Toggle menu">
              <span className={isMenuOpen ? 'open' : ''} />
              <span className={isMenuOpen ? 'open' : ''} />
              <span className={isMenuOpen ? 'open' : ''} />
            </button>
          </div>
        </div>
      </div>

      <nav
        className={`mobile-nav ${isMenuOpen ? 'mobile-nav-open' : ''}`}
        onClick={(e) => e.target.classList.contains('mobile-nav') && closeMobileMenu()}
      >
        <div className="mobile-nav-content">
          <button 
            type="button" 
            className="mobile-nav-close" 
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          {isAuthenticated ? (
            <>
              <div className="mobile-user-info">
                <div className="mobile-user-avatar">
                  {getUserProfilePicture() ? (
                    <img src={getUserProfilePicture()} alt={getUserDisplayName()} className="mobile-user-avatar-image" loading="lazy" decoding="async" />
                  ) : (
                    getUserInitials()
                  )}
                </div>
                <span className="mobile-user-name">{getUserDisplayName()}</span>
              </div>
              <div className="mobile-nav-divider" />

              <div className="mobile-nav-notifications-row">
                <NotificationsDropdown
                  isOpen={isNotifMobileOpen}
                  onToggle={() => {
                    setIsNotifMobileOpen((v) => !v);
                    setIsNotifDesktopOpen(false);
                    setIsMenuOpen(false);
                  }}
                  onClose={() => setIsNotifMobileOpen(false)}
                  mobile
                />
                <span className="mobile-nav-notifications-label">Notifications</span>
              </div>

              <Link
                to="/bags"
                className={`mobile-nav-link ${location.pathname === '/bags' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 01-8 0" />
                </svg>
                <span>My Bags</span>
              </Link>

              <Link
                to="/items"
                className={`mobile-nav-link ${location.pathname === '/items' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <path d="M9 9h6v6H9z" />
                </svg>
                <span>All Items</span>
              </Link>

              <Link
                to="/explore"
                className={`mobile-nav-link ${exploreActive ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <span>Explore</span>
              </Link>

              <Link
                to="/how-it-works"
                className={`mobile-nav-link ${howItWorksActive ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>How it Works</span>
              </Link>

              <Link
                to="/earnings"
                className={`mobile-nav-link ${location.pathname === '/earnings' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
                <span>Earnings</span>
              </Link>

              <div className="mobile-nav-divider" />

              <Link
                to="/profile"
                className={`mobile-nav-link ${location.pathname === '/profile' || location.pathname === '/account-settings' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>Profile &amp; settings</span>
              </Link>

              {user?.is_admin && (
                <Link
                  to="/admin/featured"
                  className={`mobile-nav-link ${location.pathname === '/admin/featured' ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 3l2.92 5.91 6.52.95-4.72 4.6 1.11 6.5L12 17.86l-5.83 3.1 1.11-6.5-4.72-4.6 6.52-.95L12 3z" />
                  </svg>
                  <span>Featured bags</span>
                </Link>
              )}

              {user?.is_admin && (
                <Link
                  to="/admin/properties"
                  className={`mobile-nav-link ${location.pathname === '/admin/properties' ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M8 13l2-5 2 3 1.5-2L16 12" />
                  </svg>
                  <span>Properties</span>
                </Link>
              )}

              <button type="button" className="mobile-nav-link logout" onClick={openLogoutModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                  <polyline points="16,17 21,12 16,7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Logout</span>
              </button>
            </>
          ) : (
            <>
              <Link
                to="/explore"
                className={`mobile-nav-link ${exploreActive ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Explore
              </Link>
              <Link
                to="/how-it-works"
                className={`mobile-nav-link ${howItWorksActive ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                How it Works
              </Link>

              <div className="mobile-nav-divider" />

              <button
                type="button"
                className="mobile-nav-link"
                onClick={() => {
                  closeMobileMenu();
                  openAuthModal('signin');
                }}
              >
                Sign In
              </button>
              <button
                type="button"
                className="mobile-nav-link mobile-nav-link--signup"
                onClick={() => { closeMobileMenu(); openAuthModal('signup'); }}
              >
                Sign Up
              </button>
            </>
          )}
        </div>
      </nav>

      {showLogoutModal &&
        createPortal(
          <div className="logout-modal-backdrop" onClick={closeLogoutModal} role="presentation">
            <div
              className="logout-modal-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="logout-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="logout-modal-close" onClick={closeLogoutModal} aria-label="Close">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              <div className="logout-modal-content">
                <div className="logout-modal-icon-wrap" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M7.5 17.5H4.167A.833.833 0 013.333 16.667V3.333A.833.833 0 014.167 2.5H7.5"
                      stroke="#ec3131"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.333 14.167L16.667 10l-3.334-3.333M7.5 10h9.167"
                      stroke="#ec3131"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 id="logout-modal-title" className="logout-modal-title">
                  Log Out
                </h2>
                <p className="logout-modal-message">Logging out will end your current session</p>
              </div>
              <div className="logout-modal-actions">
                <button type="button" className="logout-modal-btn logout-modal-btn--cancel" onClick={closeLogoutModal}>
                  Cancel
                </button>
                <button type="button" className="logout-modal-btn logout-modal-btn--confirm" onClick={confirmLogout}>
                  Log Out
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </header>
    </NotificationsInboxProvider>
  );
};

export default Header;
